import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Variables Supabase manquantes. Vérifie ton fichier .env');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============ AUTH ============
export const auth = {
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  },
  async signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({ email, password });
    return { data, error };
  },
  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },
  async getUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },
  onAuthChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  }
};

// ============ GAMES STORAGE ============
import { normalizeGame } from './normalizeGame';

export const gamesDB = {
  async list() {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) throw error;

    const rows = data || [];
    const emptyDataCount = rows.filter(r => !r.data || Object.keys(r.data || {}).length === 0).length;
    if (rows.length > 0 && emptyDataCount > 0) {
      console.warn(`[gamesDB] ${emptyDataCount}/${rows.length} games ont une colonne 'data' vide ou nulle.`);
    }

    return rows.map(row => {
      const payload = (row.data && typeof row.data === 'object') ? row.data : {};
      // V8.14 : normalizeGame garantit le format camelCase canonique
      // (gère les anciennes games avec champs snake_case)
      return normalizeGame({
        id: row.id,
        ...payload,
        riot_match_id: payload.riot_match_id || row.riot_match_id || null,
        durationSeconds: payload.durationSeconds ?? null,
        startTime: payload.startTime ?? null,
        created_at: row.created_at,
        updated_at: row.updated_at,
        edit_history: row.edit_history || [],
        _hasPayload: Object.keys(payload).length > 0
      });
    });
  },

  async add(game) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');
    const { data, error } = await supabase
      .from('games')
      .insert({ user_id: user.id, data: game, edit_history: [] })
      .select()
      .single();
    if (error) throw error;
    return normalizeGame({
      id: data.id,
      ...data.data,
      created_at: data.created_at,
      updated_at: data.updated_at,
      edit_history: []
    });
  },

  // v7.7.10 : insert avec dedupe vérifié EN BASE (source de vérité unique).
  async addIfNotExists(game) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');

    const matchId = game?.riot_match_id;
    if (matchId) {
      // Vérif en base juste avant l'insert
      const { data: existing, error: checkErr } = await supabase
        .from('games')
        .select('id, data')
        .eq('user_id', user.id);
      if (checkErr) throw checkErr;
      const dup = (existing || []).find(r => r.data?.riot_match_id === matchId);
      if (dup) {
        // Déjà en base → on retourne l'existant sans réinsérer
        return { __duplicate: true, id: dup.id };
      }
    }

    const { data, error } = await supabase
      .from('games')
      .insert({ user_id: user.id, data: game, edit_history: [] })
      .select()
      .single();
    if (error) throw error;
    return normalizeGame({
      id: data.id,
      ...data.data,
      created_at: data.created_at,
      updated_at: data.updated_at,
      edit_history: []
    });
  },

  async delete(id) {
    const { error } = await supabase.from('games').delete().eq('id', id);
    if (error) throw error;
  },

  async update(id, newGame, previousGame) {
    const { data: existing, error: fetchErr } = await supabase
      .from('games')
      .select('edit_history')
      .eq('id', id)
      .single();
    if (fetchErr) throw fetchErr;

    const history = existing.edit_history || [];
    const newHistoryEntry = {
      timestamp: new Date().toISOString(),
      previous: previousGame
    };
    const newHistory = [...history, newHistoryEntry].slice(-10);

    const { error } = await supabase
      .from('games')
      .update({ data: newGame, edit_history: newHistory })
      .eq('id', id);
    if (error) throw error;
    return newHistory;
  }
};

// ============ SESSIONS STORAGE ============
export const sessionsDB = {
  async list() {
    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .order('closed_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async add(sessionData) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');
    const { data, error } = await supabase
      .from('sessions')
      .insert({ user_id: user.id, ...sessionData })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { data, error } = await supabase
      .from('sessions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase.from('sessions').delete().eq('id', id);
    if (error) throw error;
  }
};

// ============ AI ANALYSES ============
export const aiAnalysesDB = {
  async list() {
    const { data, error } = await supabase
      .from('ai_analyses')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async add(analysis) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');
    const { data, error } = await supabase
      .from('ai_analyses')
      .insert({ user_id: user.id, ...analysis })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateResponse(id, responseText) {
    const { error } = await supabase
      .from('ai_analyses')
      .update({ response_text: responseText })
      .eq('id', id);
    if (error) throw error;
  },

  async delete(id) {
    const { error } = await supabase.from('ai_analyses').delete().eq('id', id);
    if (error) throw error;
  }
};

// ============ PRE-GAME INTENTS ============
export const preGameIntentsDB = {
  async list() {
    const { data, error } = await supabase
      .from('pre_game_intents')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getUnmatched() {
    // Returns the most recent unmatched intent (for auto-matching to next game)
    const { data, error } = await supabase
      .from('pre_game_intents')
      .select('*')
      .is('matched_game_id', null)
      .order('created_at', { ascending: false })
      .limit(1);
    if (error) throw error;
    return data?.[0] || null;
  },

  async add(intent) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');
    const { data, error } = await supabase
      .from('pre_game_intents')
      .insert({ user_id: user.id, ...intent })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async matchToGame(intentId, gameId) {
    const { error } = await supabase
      .from('pre_game_intents')
      .update({ matched_game_id: gameId })
      .eq('id', intentId);
    if (error) throw error;
  },

  async delete(id) {
    const { error } = await supabase.from('pre_game_intents').delete().eq('id', id);
    if (error) throw error;
  }
};

// ============ USER SETTINGS (Riot ID, préférences) ============
export const userSettingsDB = {
  async get() {
    const user = await auth.getUser();
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  async upsert(settings) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');
    const { data, error } = await supabase
      .from('user_settings')
      .upsert({ user_id: user.id, ...settings }, { onConflict: 'user_id' })
      .select()
      .single();
    if (error) throw error;
    return data;
  }
};

// ============ RANK SNAPSHOTS ============
export const rankSnapshotsDB = {
  async list() {
    const { data, error } = await supabase
      .from('rank_snapshots')
      .select('*')
      .order('captured_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async add(snapshot) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');
    const { data, error } = await supabase
      .from('rank_snapshots')
      .insert({ user_id: user.id, ...snapshot })
      .select()
      .single();
    if (error) throw error;
    return data;
  }
};

// ============ MINI BLOCKS ============
export const miniBlocksDB = {
  async list() {
    const { data, error } = await supabase
      .from('mini_blocks')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async add(block) {
    const user = await auth.getUser();
    if (!user) throw new Error('Non connecté');
    const { data, error } = await supabase
      .from('mini_blocks')
      .insert({ user_id: user.id, ...block })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id, updates) {
    const { error } = await supabase
      .from('mini_blocks')
      .update(updates)
      .eq('id', id);
    if (error) throw error;
  },

  async complete(id) {
    const { error } = await supabase
      .from('mini_blocks')
      .update({ status: 'completed', completed_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  async abandon(id) {
    const { error } = await supabase
      .from('mini_blocks')
      .update({ status: 'abandoned' })
      .eq('id', id);
    if (error) throw error;
  },

  async fail(id) {
    const { error } = await supabase
      .from('mini_blocks')
      .update({ status: 'failed', completed_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  async delete(id) {
    const { error } = await supabase.from('mini_blocks').delete().eq('id', id);
    if (error) throw error;
  }
};
